/**
 * activationTracking.js — Cold-open funnel instrumentation.
 * ────────────────────────────────────────────────────────
 * The north-star funnel was showing first_visit → first_activity_started
 * at 4.7% — a mix of a real UX blocker (the marketing popup) and a
 * measurement gap (no activity page fired the event). This module
 * fixes the measurement side.
 *
 *   markFirstActivityStarted(source, extras) — idempotent per-device.
 *
 * A "first activity" is any of:
 *   /treasure-quest  /snap  /spelling-bee  /math-snap  /coloring
 *   /academy/*       /reading/*  /games/*  /lesson/*  /draw-along/*
 *
 * The router-level `<ActivationTracker />` observes location changes
 * and fires the event automatically once, so we don't have to pepper
 * every activity page. Individual entry points may still call the
 * helper manually to include a `source` label.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const LS_FLAG = "we_first_activity_started_v1";

const ACTIVITY_PREFIXES = [
  "/treasure-quest",
  "/snap",
  "/math-snap",
  "/spelling-bee",
  "/coloring",
  "/academy/",
  "/reading/",
  "/draw-along/",
  "/games/",
  "/lesson/",
  "/quest/",
  "/live",
  "/flag-sprint",
];

function _isActivityPath(pathname) {
  return ACTIVITY_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

/** Idempotent — fires once per device across all sessions. */
export function markFirstActivityStarted(source = "unknown", extras = {}) {
  try {
    if (localStorage.getItem(LS_FLAG) === "1") return false;
    localStorage.setItem(LS_FLAG, "1");
  } catch { /* ignore — analytics is best-effort */ }
  try {
    trackEvent("first_activity_started", { source, ...extras });
  } catch { /* ignore */ }
  return true;
}

/** Router-level tracker — drop once inside <BrowserRouter>. Fires the
 *  event automatically the first time the visitor lands on any known
 *  activity path. */
export function ActivationTracker() {
  const loc = useLocation();
  useEffect(() => {
    if (_isActivityPath(loc.pathname || "/")) {
      markFirstActivityStarted("route-observer", { path: loc.pathname });
    }
  }, [loc.pathname]);
  return null;
}
